import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import ExternalIdField from './components/ExternalIdField';
import React, { useContext, useEffect } from 'react';
import {
	IEditModifierGroupsContext,
	EditModifierGroupsContext,
} from '../../../../../editModifierGroupsState/editModifierGroupsContext';
import CustomerSelect from './components/CustomerSelect';
import RestaurantsMultipleSelect from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import CategoryMultipleSelect from './components/CategoryMultipleSelect/CategoryMultipleSelect';
import SubcategoryMultipleSelect from './components/SubcategoryMultipleSelect/SubcategoryMultipleSelect';
import DishesMultipleSelect from './components/DishesMultipleSelect/DishesMultipleSelect';
import NameModifierGroupInput from './components/NameModifierGroupInput';
import ArticleNumberField from './components/ArticleNumberField';
import ModifiersList from './components/ModifiersList';
import BtnBlock from './components/BtnBlock';
import { Form } from 'antd';
import { preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setIDCustomer,
		setFetchedCustomers,
		setIDsRestaurant,
		setIDsIsForceDisabledRestaurant,
		setFetchedRestaurants,
		setIDsCategories,
		setFetchedCategories,
		updateCategories,
		setIDsSubcategories,
		setFetchedSubcategories,
		updateSubcategories,
		setIDsDishes,
		updateDishes,
		setFetchedDishes,
		changeNameOfModifierGroup,
		changeLoaderStatus,
		changeExternalId,
		generateUniqueExternalId,
		externalId,
		articleNumber,
		modifierGroupsID,
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		IDsIsForceDisabledRestaurant,
		IDsSelectedCategories,
		IDsSelectedSubcategories,
		IDsSelectedDishes,
		nameOfModifierGroup,
		modifiersOfModifierGroups,
		initialIDSelectedCustomer,
		initialIDsSelectedRestaurant,
		initialIDsSelectedCategories,
		initialIDsSelectedSubcategories,
		initialIDsSelectedDishes,
		translations,
		defaultCustomerLanguage,
	} = useContext<IEditModifierGroupsContext>(EditModifierGroupsContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({ IDSelectedCustomer });
		form.setFieldsValue({ IDsSelectedRestaurant });
		form.setFieldsValue({ IDsSelectedCategories });
		form.setFieldsValue({ IDsSelectedSubcategories });
		form.setFieldsValue({ IDsSelectedDishes });
		form.setFieldsValue({ nameOfModifierGroup });
		form.setFieldsValue({ externalId });
		form.setFieldsValue({ articleNumber });
	}, [
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		IDsSelectedCategories,
		IDsSelectedSubcategories,
		IDsSelectedDishes,
		nameOfModifierGroup,
		externalId,
		articleNumber,
	]);

	const submit = () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		setDataForSubmit({
			id: modifierGroupsID,
			name: nameOfModifierGroup,
			customerId: IDSelectedCustomer || '',
			restaurants: IDsSelectedRestaurant.map((el) => ({
				restaurantId: el,
				forceDisable: IDsIsForceDisabledRestaurant.includes(el),
			})),
			dishIds: IDsSelectedDishes,
			externalId: externalId ? externalId : null,
			translations,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<CustomerSelect
						initialIDSelectedCustomer={initialIDSelectedCustomer}
						setIDCustomer={setIDCustomer}
						setFetchedCustomers={setFetchedCustomers}
					/>
					<RestaurantsMultipleSelect
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						IDsIsForceDisabledRestaurant={IDsIsForceDisabledRestaurant}
						IDSelectedCustomer={IDSelectedCustomer}
						initialIDsSelectedRestaurant={initialIDsSelectedRestaurant}
						setIDsRestaurant={setIDsRestaurant}
						setIDsIsForceDisabledRestaurant={setIDsIsForceDisabledRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
					/>
					<NameModifierGroupInput changeNameOfModifierGroup={changeNameOfModifierGroup} />
					<ModifiersList modifiersOfModifierGroups={modifiersOfModifierGroups} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="articleNumberHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<ArticleNumberField />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="idHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="categoriesSubcategoriesDishesHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<CategoryMultipleSelect
						setIDsCategories={setIDsCategories}
						setFetchedCategories={setFetchedCategories}
						updateCategories={updateCategories}
						changeLoaderStatus={changeLoaderStatus}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						initialIDsSelectedCategories={initialIDsSelectedCategories}
						IDsSelectedCategories={IDsSelectedCategories}
					/>
					<SubcategoryMultipleSelect
						setIDsSubcategories={setIDsSubcategories}
						setFetchedSubcategories={setFetchedSubcategories}
						updateSubcategories={updateSubcategories}
						changeLoaderStatus={changeLoaderStatus}
						IDsSelectedCategories={IDsSelectedCategories}
						initialIDsSelectedSubcategories={initialIDsSelectedSubcategories}
						IDsSelectedSubcategories={IDsSelectedSubcategories}
					/>
					<DishesMultipleSelect
						setIDsDishes={setIDsDishes}
						setFetchedDishes={setFetchedDishes}
						updateDishes={updateDishes}
						changeLoaderStatus={changeLoaderStatus}
						initialIDsSelectedDishes={initialIDsSelectedDishes}
						IDsSelectedDishes={IDsSelectedDishes}
						IDsSelectedSubcategories={IDsSelectedSubcategories}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
